import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QuestionType } from '../../../generated/hooks';
import { AnswerGroupTable, ColumnType, type TableColumn } from '../components/AnswerGroupTable';
import { questionSummaryKey } from '@arnold/core';
import {
  localizedText,
  newDefinitionId,
  type ReportTableColumn,
  type ReportTableConfiguration,
} from '../definition/types';
import { comparisonId, serializeComparisons } from '../definition/tableComparisons';
import { answeredRows } from '../definition/tableRows';
import { useDefinitionMetrics } from '../definition/ReportDefinitionContext';
import { isScoringValid } from '../questionScoring';
import { CURRENT_REPORT_GROUP_ID } from '../comparisonGroups';
import type { NewReport } from '../hooks/useNewReport';
import type { AnswerGroup, AnswerGroupRowMode } from '../types';
import { getPresetColumns, getPresetQuestion, isTextColumn, TABLE_PRESETS, type TablePreset } from './tableTestColumns';
import { TableColumnComposer } from './TableColumnComposer';
import { ComparisonGroupPicker } from './ComparisonGroupPicker';
import {
  Action,
  Badge,
  Bar,
  Check,
  ColumnBinding,
  ColumnCard,
  ColumnList,
  ColumnTitle,
  Field,
  Hint,
  IconAction,
  Page,
  Panel,
  PanelHeader,
  PresetButton,
  PresetGrid,
  PropertiesGrid,
  PropertiesPanel,
  Segment,
  Segments,
  Stack,
  Workspace,
} from './tablePlaygroundStyles';

const EMPTY_GROUPS: AnswerGroup[] = [];
const ROW_MODES: AnswerGroupRowMode[] = ['teams', 'teamsAndParticipants', 'participants'];
const isMetric = (column: TableColumn) => [ColumnType.NPS, ColumnType.MINI_NPS, ColumnType.SCORE].includes(column.type);

export const TableTestPage = ({
  report,
  initialConfiguration,
  onConfigurationChange,
  embedded = false,
  language,
}: {
  report: NewReport;
  initialConfiguration?: ReportTableConfiguration;
  onConfigurationChange?: (configuration: ReportTableConfiguration) => void;
  embedded?: boolean;
  language?: string;
}) => {
  const { t, i18n } = useTranslation('newReport');
  const labelLanguage = language ?? i18n.language;
  const { selectRows, selectComparisonGroups, questionScoring, adHocGroups } = report;
  const metrics = useDefinitionMetrics();
  const questions = useMemo(
    () =>
      (report.rawData?.questions ?? []).filter((question) =>
        [QuestionType.Select, QuestionType.Multiselect, QuestionType.Scale, QuestionType.Freetext].includes(
          question.type,
        ),
      ),
    [report.rawData],
  );
  const [configs, setConfigs] = useState<ReportTableColumn[]>(
    () =>
      initialConfiguration?.columns ??
      getPresetColumns('metrics', questions, getPresetQuestion('metrics', questions), metrics),
  );
  const [rowMode, setRowMode] = useState<AnswerGroupRowMode>(initialConfiguration?.rowMode ?? 'teams');
  const [preset, setPreset] = useState<TablePreset | null>(initialConfiguration ? null : 'metrics');
  const [pageSize, setPageSize] = useState(initialConfiguration?.pageSize ?? 10);
  const [resizeEnabled, setResizeEnabled] = useState(initialConfiguration?.columnResizeEnabled ?? true);
  const [scrollEnabled, setScrollEnabled] = useState(initialConfiguration?.horizontalScroll ?? false);
  const [sortingEnabled, setSortingEnabled] = useState(initialConfiguration?.sortingEnabled ?? true);
  const [searchEnabled, setSearchEnabled] = useState(initialConfiguration?.searchEnabled ?? true);
  const [columnOrderingEnabled, setColumnOrderingEnabled] = useState(
    initialConfiguration?.columnOrderingEnabled ?? true,
  );
  const [textFilterColumnId, setTextFilterColumnId] = useState(initialConfiguration?.nonEmptyTextColumnId ?? '');
  const [responseQuestionIndex, setResponseQuestionIndex] = useState(initialConfiguration?.responseQuestionIndex ?? '');
  const [comparing, setComparing] = useState(initialConfiguration?.comparisonGroups !== undefined);
  const [comparisonIds, setComparisonIds] = useState<Set<string>>(
    () => new Set(initialConfiguration?.comparisonGroups?.map(comparisonId) ?? [CURRENT_REPORT_GROUP_ID]),
  );
  // A flat participant union would erase the boundaries the user is comparing.
  const effectiveRowMode = comparing && rowMode === 'participants' ? 'teamsAndParticipants' : rowMode;
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [composer, setComposer] = useState<'new' | TableColumn | null>(null);
  const [lastAddedId, setLastAddedId] = useState<string | null>(null);
  const editingId = composer && composer !== 'new' ? composer.id : null;
  const commentQuestions = useMemo(
    () =>
      new Set(
        (report.rawData?.responses ?? []).flatMap((response) =>
          (response.messages ?? [])
            .filter((message) => message.comment?.trim())
            .map((message) => message.questionDefinitionIndex),
        ),
      ),
    [report.rawData],
  );
  const roots = useMemo(
    () => (comparing ? selectComparisonGroups(comparisonIds) : report.filteredData ?? EMPTY_GROUPS),
    [comparing, selectComparisonGroups, comparisonIds, report.filteredData],
  );
  const projectedRows = useMemo(() => selectRows(roots, effectiveRowMode), [selectRows, roots, effectiveRowMode]);
  const textColumns = useMemo(() => configs.filter(isTextColumn), [configs]);
  const textFilterColumn = textColumns.find((column) => column.id === textFilterColumnId);
  const rows = useMemo(() => {
    const visibleRows = responseQuestionIndex ? answeredRows(projectedRows, responseQuestionIndex) : projectedRows;
    if (!textFilterColumn) return visibleRows;
    const keepContent = (group: AnswerGroup): AnswerGroup | null => {
      const summary = group.questionSummaries.get(
        questionSummaryKey(textFilterColumn.questionIndex, textFilterColumn.statementIndex),
      );
      const entries = textFilterColumn.source === 'comment' ? summary?.commentEntries : summary?.textEntries;
      if (!entries?.length) return null;
      return {
        ...group,
        children: group.children.map(keepContent).filter((child): child is AnswerGroup => child !== null),
      };
    };
    return visibleRows.map(keepContent).filter((group): group is AnswerGroup => group !== null);
  }, [projectedRows, textFilterColumn, responseQuestionIndex]);

  const columnLabel = useCallback(
    (column: ReportTableColumn) => {
      const translation = localizedText(column.translations, labelLanguage);
      if (translation) return translation;
      if (column.label) return column.label;
      if ('metricId' in column)
        return localizedText(metrics.find((metric) => metric.id === column.metricId)?.title, labelLanguage);
      let label = t(`table.columns.${column.type}`);
      if (!('questionIndex' in column)) return label;
      const question = questions.find((item) => item.index === column.questionIndex);
      const nps = questionScoring[column.questionIndex]?.nps;
      if ([ColumnType.NPS, ColumnType.MINI_NPS].includes(column.type) && nps)
        label = t('tableTest.npsColumnLabel', { label, min: nps.minValue ?? 0 });
      if (isTextColumn(column) && column.source === 'comment') label = t('tableTest.commentColumnLabel', { label });
      return [
        label,
        question?.text ?? column.questionIndex,
        column.statementIndex === undefined ? undefined : question?.statementsLocal?.[column.statementIndex],
      ]
        .filter(Boolean)
        .join(' · ');
    },
    [questions, questionScoring, metrics, labelLanguage, t],
  );
  const columns = useMemo(
    () =>
      configs.map((column) => {
        const format =
          'metricId' in column
            ? metrics.find((metric) => metric.id === column.metricId)?.presentation.format
            : undefined;
        return { ...column, ...(format && { format }), label: columnLabel(column) };
      }),
    [configs, columnLabel, metrics],
  );
  const columnOrder = useMemo(() => columns.map((column) => column.id), [columns]);
  const changeColumnOrder = (ids: string[]) => {
    const positions = new Map(ids.map((id, index) => [id, index]));
    setConfigs((previous) =>
      [...previous].sort((a, b) => (positions.get(a.id) ?? ids.length) - (positions.get(b.id) ?? ids.length)),
    );
    setPreset(null);
  };
  const needsValues = (column: TableColumn) => {
    if (!isMetric(column) || !('questionIndex' in column)) return false;
    const question = questions.find((item) => item.index === column.questionIndex);
    return (
      !question ||
      !isScoringValid(
        question,
        questionScoring[column.questionIndex]?.[column.type === ColumnType.SCORE ? 'average' : 'nps'],
      )
    );
  };
  const applyPreset = (next: TablePreset) => {
    setPreset(next);
    setConfigs(getPresetColumns(next, questions, getPresetQuestion(next, questions), metrics));
    setTextFilterColumnId('');
    setRowMode(next === 'text' ? 'participants' : 'teams');
    setScrollEnabled(next === 'wide');
    setComposer(null);
    setLastAddedId(null);
    setLayoutVersion((version) => version + 1);
  };
  const addColumn = (column: ReportTableColumn) => {
    const added = { ...column, id: newDefinitionId() };
    setConfigs((previous) => [...previous, added]);
    setLastAddedId(added.id);
    setPreset(null);
  };
  const moveColumn = (index: number, direction: number) => {
    setConfigs((previous) => {
      const next = [...previous];
      [next[index], next[index + direction]] = [next[index + direction], next[index]];
      return next;
    });
    setPreset(null);
  };
  const removeColumn = (id: string) => {
    setConfigs((previous) => previous.filter((column) => column.id !== id));
    if (composer && composer !== 'new' && composer.id === id) setComposer(null);
    setPreset(null);
  };
  const savedGroupIds = useMemo(() => new Set([...adHocGroups.values()].map((group) => group.id)), [adHocGroups]);
  const followsHeaderFilters = !comparing || [...comparisonIds].some((id) => !savedGroupIds.has(id));
  const resetKey = JSON.stringify([
    report.rawData?.id,
    effectiveRowMode,
    responseQuestionIndex,
    comparing,
    [...comparisonIds].sort(),
    followsHeaderFilters ? [[...report.filters.teamIds].sort(), report.filters.answerSelections] : null,
    textFilterColumn
      ? [textFilterColumn.id, textFilterColumn.questionIndex, textFilterColumn.statementIndex, textFilterColumn.source]
      : null,
  ]);

  const configuration = useMemo(
    (): ReportTableConfiguration => ({
      columns: configs,
      rowMode: effectiveRowMode,
      pageSize,
      columnResizeEnabled: resizeEnabled,
      horizontalScroll: scrollEnabled,
      sortingEnabled,
      searchEnabled,
      columnOrderingEnabled,
      nonEmptyTextColumnId: textFilterColumn?.id,
      responseQuestionIndex: responseQuestionIndex || undefined,
      comparisonGroups: comparing
        ? serializeComparisons(
            comparisonIds,
            report.adHocGroupConfigs,
            labelLanguage,
            initialConfiguration?.comparisonGroups,
          )
        : undefined,
    }),
    [
      configs,
      effectiveRowMode,
      pageSize,
      resizeEnabled,
      scrollEnabled,
      sortingEnabled,
      searchEnabled,
      columnOrderingEnabled,
      textFilterColumn?.id,
      responseQuestionIndex,
      comparing,
      comparisonIds,
      report.adHocGroupConfigs,
      labelLanguage,
      initialConfiguration?.comparisonGroups,
    ],
  );
  useEffect(() => onConfigurationChange?.(configuration), [configuration, onConfigurationChange]);

  return (
    <Page>
      {!embedded && (
        <Stack>
          <h1>{t('tableTest.title')}</h1>
          <Hint>{t('tableTest.intro')}</Hint>
        </Stack>
      )}
      {!embedded && (
        <Panel>
          <Stack>
            <h2>{t('tableTest.examplePresets')}</h2>
            <Hint>{t('tableTest.examplePresetsHint')}</Hint>
            <PresetGrid>
              {TABLE_PRESETS.map((value) => (
                <PresetButton
                  type="button"
                  key={value}
                  active={preset === value}
                  aria-pressed={preset === value}
                  onClick={() => applyPreset(value)}
                >
                  <strong>{t(`tableTest.presets.${value}`)}</strong>
                  <span>{t(`tableTest.presetHints.${value}`)}</span>
                </PresetButton>
              ))}
            </PresetGrid>
          </Stack>
        </Panel>
      )}
      <Stack>
        <Panel>
          <Stack>
            <Bar>
              <Badge>1</Badge>
              <h2>{t('tableTest.chooseRows')}</h2>
            </Bar>
            <Bar>
              <Segments>
                <Segment
                  type="button"
                  active={!comparing}
                  aria-pressed={!comparing}
                  onClick={() => setComparing(false)}
                >
                  {t('tableTest.viewAll')}
                </Segment>
                <Segment type="button" active={comparing} aria-pressed={comparing} onClick={() => setComparing(true)}>
                  {t('tableTest.compareGroups')}
                </Segment>
              </Segments>
            </Bar>
            <Bar>
              <Hint>{t('tableTest.showRowsAs')}</Hint>
              <Segments>
                {ROW_MODES.filter((mode) => !comparing || mode !== 'participants').map((mode) => (
                  <Segment
                    key={mode}
                    type="button"
                    active={effectiveRowMode === mode}
                    aria-pressed={effectiveRowMode === mode}
                    onClick={() => setRowMode(mode)}
                  >
                    {t(`tableTest.${comparing ? 'comparisonRowModes' : 'rowModes'}.${mode}`)}
                  </Segment>
                ))}
              </Segments>
            </Bar>
            <Bar>
              <Hint>{t('tableTest.currentRows', { count: rows.length })}</Hint>
              {textFilterColumn && (
                <Badge>{t('tableTest.activeTextFilter', { column: columnLabel(textFilterColumn) })}</Badge>
              )}
            </Bar>
            {comparing ? (
              <ComparisonGroupPicker report={report} selectedIds={comparisonIds} setSelectedIds={setComparisonIds} />
            ) : (
              <Hint>{t('tableTest.viewAllHint')}</Hint>
            )}
          </Stack>
        </Panel>
        <Panel>
          <PanelHeader>
            <Bar>
              <Badge>2</Badge>
              <h2>{t('tableTest.yourColumns')}</h2>
              <Badge>{configs.length}</Badge>
            </Bar>
            <Action primary type="button" onClick={() => setComposer('new')}>
              + {t('tableTest.addColumn')}
            </Action>
          </PanelHeader>
          <Workspace open={composer !== null}>
            <Stack>
              <ColumnList>
                {configs.map((column, index) => (
                  <ColumnCard key={column.id} selected={column.id === editingId || column.id === lastAddedId}>
                    <Bar>
                      <Badge>{index + 1}</Badge>
                      <ColumnTitle>
                        {column.type === ColumnType.CUSTOM_METRIC && column.presentation
                          ? t(`definition.cellViews.${column.presentation}`)
                          : t(`table.columns.${column.type}`)}
                      </ColumnTitle>
                    </Bar>
                    <ColumnBinding title={columnLabel(column)}>
                      {'questionIndex' in column || 'metricId' in column
                        ? columnLabel(column)
                        : t(`tableTest.columnHints.${column.type}`)}
                    </ColumnBinding>
                    <Bar>
                      <IconAction
                        type="button"
                        disabled={index === 0}
                        onClick={() => moveColumn(index, -1)}
                        title={t('tableTest.moveLeft')}
                        aria-label={t('tableTest.moveLeft')}
                      >
                        ←
                      </IconAction>
                      <IconAction
                        type="button"
                        disabled={index === configs.length - 1}
                        onClick={() => moveColumn(index, 1)}
                        title={t('tableTest.moveRight')}
                        aria-label={t('tableTest.moveRight')}
                      >
                        →
                      </IconAction>
                      <Action primary={needsValues(column)} type="button" onClick={() => setComposer(column)}>
                        {t(needsValues(column) ? 'tableTest.setValues' : 'tableTest.edit')}
                      </Action>
                      <IconAction
                        type="button"
                        onClick={() => removeColumn(column.id)}
                        title={t('tableTest.remove')}
                        aria-label={t('tableTest.remove')}
                      >
                        ×
                      </IconAction>
                    </Bar>
                  </ColumnCard>
                ))}
              </ColumnList>
              {!configs.length && <Hint>{t('tableTest.emptyColumnsHint')}</Hint>}
            </Stack>
            {composer && (
              <TableColumnComposer
                key={composer === 'new' ? 'new' : composer.id}
                report={report}
                language={labelLanguage}
                questions={questions}
                commentQuestions={commentQuestions}
                editingColumn={composer === 'new' ? undefined : composer}
                onAdd={(column) => {
                  if (composer !== 'new') {
                    setConfigs((previous) =>
                      previous.map((item) => (item.id === composer.id ? { ...column, id: item.id } : item)),
                    );
                    setComposer(null);
                  } else {
                    addColumn(column);
                    setComposer(null);
                  }
                }}
                onClose={() => setComposer(null)}
              />
            )}
          </Workspace>
        </Panel>
        <PropertiesPanel as="aside">
          <PanelHeader>
            <h3>{t('tableTest.tableProperties')}</h3>
            <Action
              type="button"
              onClick={() => {
                if (onConfigurationChange)
                  setConfigs((previous) =>
                    previous.map((column) => ({
                      ...column,
                      width: initialConfiguration?.columns.find((item) => item.id === column.id)?.width,
                    })),
                  );
                setLayoutVersion((version) => version + 1);
              }}
            >
              {t('tableTest.resetLayout')}
            </Action>
          </PanelHeader>
          <PropertiesGrid>
            <Stack>
              <Check>
                <input
                  type="checkbox"
                  checked={sortingEnabled}
                  onChange={(event) => setSortingEnabled(event.target.checked)}
                />
                {t('tableTest.sorting')}
              </Check>
              <Check>
                <input
                  type="checkbox"
                  checked={searchEnabled}
                  onChange={(event) => setSearchEnabled(event.target.checked)}
                />
                {t('tableTest.search')}
              </Check>
              <Check>
                <input
                  type="checkbox"
                  checked={columnOrderingEnabled}
                  onChange={(event) => setColumnOrderingEnabled(event.target.checked)}
                />
                {t('tableTest.columnOrdering')}
              </Check>
            </Stack>
            <Stack>
              <Check>
                <input
                  type="checkbox"
                  checked={resizeEnabled}
                  onChange={(event) => setResizeEnabled(event.target.checked)}
                />
                {t('tableTest.resize')}
              </Check>
              <Check>
                <input
                  type="checkbox"
                  checked={scrollEnabled}
                  onChange={(event) => setScrollEnabled(event.target.checked)}
                />
                {t('tableTest.scroll')}
              </Check>
              <Hint>{t(scrollEnabled ? 'tableTest.scrollHint' : 'tableTest.fitHint')}</Hint>
            </Stack>
            <Field>
              {t('tableTest.pageSize')}
              <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
                {[2, 5, 10, 25, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </Field>
            <Field>
              {t('definition.onlyAnswered')}
              <select value={responseQuestionIndex} onChange={(event) => setResponseQuestionIndex(event.target.value)}>
                <option value="">{t('definition.allRows')}</option>
                {questions.map((question) => (
                  <option key={question.index} value={question.index}>
                    {question.text}
                  </option>
                ))}
              </select>
            </Field>
            {textColumns.length > 0 && (
              <Field>
                {t('tableTest.textRowFilter')}
                <select
                  value={textFilterColumn?.id ?? ''}
                  onChange={(event) => setTextFilterColumnId(event.target.value)}
                >
                  <option value="">{t('tableTest.noTextRowFilter')}</option>
                  {textColumns.map((column) => (
                    <option key={column.id} value={column.id}>
                      {columnLabel(column)}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </PropertiesGrid>
        </PropertiesPanel>
      </Stack>
      <h2>{t('definition.preview')}</h2>
      <AnswerGroupTable
        key={layoutVersion}
        groups={rows}
        columns={columns}
        pageSize={pageSize}
        horizontalScroll={scrollEnabled}
        columnResizeEnabled={resizeEnabled}
        sortingEnabled={sortingEnabled}
        searchEnabled={searchEnabled}
        columnOrderingEnabled={columnOrderingEnabled}
        columnOrder={columnOrder}
        onColumnOrderChange={changeColumnOrder}
        onColumnWidthsChange={
          onConfigurationChange
            ? (widths) =>
                setConfigs((previous) =>
                  previous.map((column) => ({ ...column, width: widths[column.id] ?? column.width })),
                )
            : undefined
        }
        resetKey={resetKey}
        label={t('tableTest.title')}
      />
    </Page>
  );
};
